import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseGetRequest,
  CurrencyModel,
  FundingModel,
  FundingShareValueModel,
  WhereGetRequest,
} from '@bp-core/src/lib/proto/common-message.pb';
import { CurrencyGrpcServiceClient } from '@bp-core/src/lib/proto/currency.pbsc';
import { FundingShareValueGetRequest } from '@bp-core/src/lib/proto/funding-share-value.pb';
import {
  GetAllProfitabilitiesRequest,
  ProfitabilitiesResponse,
} from '@bp-core/src/lib/proto/portal-goal.pb';
import { PortalGoalGrpcServiceClient } from '@bp-core/src/lib/proto/portal-goal.pbsc';
import { BpCoreModule } from '@bp-core/src/public-api';
import { environment } from '@environment/environment';
import { fuseAnimations } from '@fuse/animations';
import { BoolValue, StringValue } from '@ngx-grpc/well-known-types';
import { dataPerPeriodCache } from 'bp/shared/models/data-per-period-cache.type';
import { GoalFundingService } from 'bp/shared/services/goal-funding.service';
import dayjs from 'dayjs';
import { FundingShareValueGrpcServiceClient } from 'projects/bp-core/src/lib/proto/funding-share-value.pbsc';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { DiscoverFundAvatarComponent } from '../discover-fund-avatar/discover-fund-avatar.component';
import { FundingShareValueGraphComponent } from '../funding-share-value-graph/funding-share-value-graph.component';

@Component({
  selector: 'app-funds-detail-search-funds',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    BpCoreModule,
    FundingShareValueGraphComponent,
    DiscoverFundAvatarComponent,
    MatExpansionModule,
    MatTooltipModule,
  ],
  templateUrl: './funds-detail-search-funds.component.html',
  styleUrls: ['./funds-detail-search-funds.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: fuseAnimations,
})
export class FundsDetailSearchFundsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() funding: FundingModel | undefined;
  @Input() isCustomFundAdded: boolean;
  @Input() isFundingDetail: boolean = false;
  @Output() addCustomCategoryFund = new EventEmitter<void>();
  @Output() removeCustomCategoryFund = new EventEmitter<void>();

  urlFileBase = environment.urlApiV2 + 'api/Files/';
  token = localStorage.getItem('access_token');
  unsubscribe$ = new Subject<void>();
  fundingShareValues: FundingShareValueModel[] | undefined;
  currencyFund: CurrencyModel | undefined;
  profitabilityValue: ProfitabilitiesResponse.Data | undefined;
  periodTime: '1m' | 'thisYear' | '1y' | 'all' = '1m';
  isLoading = false;
  heightChart = 220;
  dataPerPeriodCache: Record<typeof this.periodTime, dataPerPeriodCache | undefined> = {
    '1m': undefined,
    'thisYear': undefined,
    '1y': undefined,
    'all': undefined,
  };
  periodTimeTitle: Record<typeof this.periodTime, string> = {
    '1m': '1 mes',
    'thisYear': 'este año',
    '1y': '1 año',
    'all': 'Todo',
  };
  canInvest = computed(() => {
    return this.evaluateInvestmentEligibility();
  });
  constructor(
    public goalFundingService: GoalFundingService,
    private fundingShareValueGrpcServiceClient: FundingShareValueGrpcServiceClient,
    private currencyGrpcServiceClient: CurrencyGrpcServiceClient,
    private portalGoalGrpcServiceClient: PortalGoalGrpcServiceClient,

    private cd: ChangeDetectorRef,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.funding?.currentValue) {
      this.dataPerPeriodCache = {
        '1m': undefined,
        'thisYear': undefined,
        '1y': undefined,
        'all': undefined,
      };
      this.getShareValues(this.funding?.id!);
      this.getCurrencyFund(this.funding?.currencyId!);
    }
  }

  ngOnInit(): void {
    // if (this.funding?.id) {
    //   this.getShareValues(this.funding?.id);
    //   this.getCurrencyFund(this.funding?.currencyId);
    // }
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getBrochureUrl(fund: FundingModel) {
    if (fund?.informativeBrochureUrl !== '') {
      return fund.informativeBrochureUrl;
    }
    return this.urlFileBase + fund?.informativeBrochureId?.value + '/?token=' + this.token;
  }
  getLastShareValue() {
    return this.fundingShareValues?.[this.fundingShareValues.length - 1].value;
  }
  getFundingTags(funding: FundingModel) {
    console.log(funding);
    return (
      funding?.fundingTags?.sort((a, b) => a.order - b.order).filter(tag => tag.tag?.show) ?? []
    );
  }
  getShareValues(fundingId: number, periodTime: '1m' | 'thisYear' | '1y' | 'all' = '1y') {
    this.isLoading = true;
    this.periodTime = periodTime;
    if (this.dataPerPeriodCache[periodTime]) {
      this.fundingShareValues = this.dataPerPeriodCache[periodTime]?.fundingShareValues;
      this.profitabilityValue = this.dataPerPeriodCache[periodTime]?.profitabilityValue;
      this.isLoading = false;
      return;
    }

    let periodWhere = '';
    let periodString: string | undefined = '';
    switch (periodTime) {
      case '1y': {
        periodString = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
        periodWhere = `date>="${periodString}" AND date<="${dayjs().format('YYYY-MM-DD')}"`;
        break;
      }
      case 'thisYear': {
        periodString = dayjs().year().toString() + '-01-01';
        periodWhere = `date>="${periodString}" AND date<="${dayjs().format('YYYY-MM-DD')}"`;
        break;
      }
      case 'all': {
        periodWhere = '';
        periodString = undefined;
        break;
      }

      default: {
        periodString = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
        periodWhere = `date>="${periodString}" AND date<="${dayjs().format('YYYY-MM-DD')}"`;

        break;
      }
    }

    combineLatest([
      this.fundingShareValueGrpcServiceClient.get(
        new FundingShareValueGetRequest({
          page: 1,
          where: `fundingId=${fundingId} ${periodWhere !== '' ? 'AND ' + periodWhere : ''}`,
          order: `date desc`,
        }),
      ),
      this.portalGoalGrpcServiceClient.getAllProfitabilities(
        new GetAllProfitabilitiesRequest({
          funds: [fundingId],
          fromDate: periodString,
          toDate: dayjs().format('YYYY-MM-DD'),
          adjustDates: new BoolValue({ value: periodTime == 'all' }),
          onlyFunds: new BoolValue({ value: true }),
          onlyResume: new BoolValue({ value: true }),
        }),
      ),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ([fundingShareValues, profitabilityValue]) => {
          this.fundingShareValues = fundingShareValues.values?.sort((a, b) => {
            return a.date!.toDate().getTime() - b.date!.toDate().getTime();
          });
          this.profitabilityValue = profitabilityValue.values?.[0];
          this.dataPerPeriodCache[periodTime] = {
            fundingShareValues: this.fundingShareValues,
            profitabilityValue: this.profitabilityValue,
          };
          this.cd.detectChanges();
        },
        error: error => {
          console.error(error);
        },
        complete: () => {
          this.isLoading = false;
          this.cd.markForCheck();
        },
      });
  }
  scrollToExpansionPanel(elementToScroll: HTMLElement) {
    setTimeout(() => {
      elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    // setTimeout(() => {
    //   window.scrollTo({
    //     top: document.body.scrollHeight,
    //     behavior: 'smooth',
    //   });
    // }, 200);
  }
  private getCurrencyFund(currencyId: number) {
    this.currencyGrpcServiceClient
      .get(
        new BaseGetRequest({
          where: new WhereGetRequest({
            predicate: new StringValue({
              value: `id=${currencyId}`,
            }),
          }),
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: currency => {
          this.currencyFund = currency;
          this.cd.markForCheck();
        },
        error: error => {
          console.error(error);
        },
      });
  }
  private evaluateInvestmentEligibility(): { canInvest: boolean; description: string } {
    const lockTags =
      this.funding?.fundingTags?.filter(tag => tag.tag?.lockFund).flatMap(tag => tag.tag!) ?? [];

    if (lockTags.length > 0) {
      if (lockTags.length == 1) {
        return {
          canInvest: false,
          description:
            `${lockTags[0].tooltipText?.value}\nPara más información, contacta a tu asesor.` ||
            `No puedes invertir en este fondo porque está bloqueado por la etiqueta: ${lockTags[0].name}`,
        };
      }

      return {
        canInvest: false,
        description: `Fondo no disponible por las siguientes razones: \n\n - ${lockTags.map(tag => tag.tooltipText?.value).join('\n- ')}\n\nPara más información, contacta a tu asesor.`,
      };
    }

    // Aquí se pueden agregar más validaciones en el futuro:
    // - Estado de la cuenta del usuario
    // - Límites de inversión
    // - Verificación de documentos
    // - Restricciones geográficas
    // - etc.

    return {
      canInvest: true,
      description: '',
    };
  }
}
